<template>
    <div class="w-full bg-gradient-to-b from-green-500 to-green-600 border-b-2 border-green-600 shadow-lg">
        <div class="py-3 container mx-auto flex items-center justify-between w-full">
            <div class="flex items-center">
                <div @click="$modal.show('project-cards')" class="group flex flex-col text-white hover:text-green-100 leading-normal cursor-pointer">
                    <div class="flex">
                        <div>
                            <div class="text-2xl font-semibold uppercase">{{ project.name }}</div>
                        </div>
                        <div class="flex">
                            <inline-svg name="chevron-down" classes="fill-current text-white group-hover:text-green-100 h-5 w-5 ml-3 cursor-pointer"></inline-svg>
                        </div>
                    </div>
                </div>

                <div class="ml-12">
                    <div class="container flex items-center justify-betwee">
                        <div class="flex flex-1 items-center gap-x-8">
                            <div class="flex items-center gap-x-4">
                                <div class="badge border rounded space-x-2" :class="project.status_color">
                                    <inline-svg v-if="project.status == 'Archived'" name="archived" classes="w-4 h-4 fill-current text-white"></inline-svg>
                                    <span class="font-variant-all-small-caps">{{ project.status }}</span>
                                </div>

                                <span class="badge border font-variant-all-small-caps" :class="project.priority_color">
                                    {{ project.priority }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-x-4">
                <a v-if="showBackButton" :href="`/projects/${project.id}`" class="flex items-center gap-x-2 btn btn-small btn-green">
                    <inline-svg name="chevron-left" classes="w-5 h-5 text-white fill-current" />
                    Back to Project
                </a>
                <div class="group/dropdown dropdown px-4 py-2 bg-gray-50 rounded-md border border-gray-200">
                    <div class="flex items-center">
                        <span class="text-gray-600 group-hover/dropdown:text-gray-500 font-semibold">Manage Project</span>
                        <inline-svg name="chevron-down" classes="ml-1 fill-current text-gray-500 w-6 h-6"></inline-svg>
                    </div>
                    <div class="dropdown-menu">
                        <ul>
                            <li v-if="canManageComps" class="group space-x-2 hover:bg-gray-100">
                                <inline-svg name="comp" classes="text-gray-300 group-hover:text-green-600 fill-current w-4 h-4"></inline-svg>
                                <button @click="$modal.show('project-comps')" type="button" class="text-base text-gray-500">Comps</button>
                            </li>
                            <li v-if="canManageSites" class="group space-x-2 hover:bg-gray-100">
                                <inline-svg name="site" classes="text-gray-300 group-hover:text-blue-600 fill-current w-4 h-4"></inline-svg>
                                <button @click="$modal.show('project-sites')" type="button" class="text-base text-gray-500">Sites</button>
                            </li>
                            <li v-if="canDeleteProject" class="group space-x-2 hover:bg-gray-100">
                                <inline-svg name="trash" classes="text-gray-300 group-hover:text-red-500 fill-current w-4 h-4"></inline-svg>
                                <button @click="confirmDeleteProject" type="button" class="text-base text-gray-500">Delete Project</button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <modal @opened="$eventHub.$emit('convert-currency')" name="project-cards" width="1300px" height="auto" click-to-close scrollable adaptive draggable=".modal-header">
            <div class="py-4 px-8">
                <project-cards
                    :project-data="projects.all"
                    small-header
                    :show-filters="false"
                    view="table">
                </project-cards>
            </div>
        </modal>

        <modal @opened="$eventHub.$emit('convert-currency')" name="project-sites" width="70%" height="auto" click-to-close scrollable adaptive draggable=".modal-header">
            <manage-project-sites
                :project-id="project.id"
                :site-data="project.sites"
                @sites-updated="onSitesUpdated"
                :per-page="5"
                small-header
            >
            </manage-project-sites>
        </modal>

        <modal name="edit-project" width="70%" height="auto" click-to-close scrollable adaptive draggable=".modal-header">
            <edit-project :project-data="project"></edit-project>
        </modal>

        <modal name="project-comps" width="65%" height="auto" :clickToClose="true" :scrollable="true" draggable=".modal-header">
            <div>
                <manage-project-comps
                    :project-id="project.id"
                    :comp-data="project.comps"
                    small-header>
                </manage-project-comps>
            </div>
        </modal>
    </div>
</template>

<script>

    export default {
        props: {
            projects: {
                type: [Object, Array],
                required: true
            },

            project: {
                type: Object,
                required: true
            },

            showBackButton: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {

            }
        },

        computed: {

            canManageComps() {
                return this.$gate.can('update', 'project', this.project.id);
            },
            
            canManageSites() {
                return this.$gate.can('update', 'project', this.project.id);
            },
            
            canDeleteProject() {
                return this.$gate.can('delete', 'project', this.project.id);
            },
        },

        methods: {
            onSitesUpdated(sites) {
                // The parent owns `project`; hand the new list up rather than
                // writing through the prop.
                this.$emit('project-updated', { ...this.project, sites });
            },

            save() {
                this.loading = true;

                axios.patch('/projects/' + this.project.id, this.project).then(response => {
                    this.old = response.data.project;
                    this.$emit('project-updated', response.data.project);
                    // `projects` is the Vuex state object handed down as a prop;
                    // write to the store directly rather than through the prop.
                    this.$store.state.projects.selectedProject = response.data.project;

                    this.editing = false;

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    })
                }).catch(error => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                }).finally(() => {
                    this.loading = false;
                });
            },

            confirmDeleteProject() {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this project?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteProject();
                    }
                });
            },

            deleteProject() {
                axios.delete('/projects/' + this.project.id)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Project Deleted'
                        });

                        setTimeout(() => {
                            window.location = '/projects'
                        }, 1000);
                    })
                    .catch(error => {
                            this.$notify({
                                type: 'error',
                                title: 'Error',
                                text: error.response.data.message
                            });
                        }
                    );
            }
        }
    }
</script>
